import { DestroyRef, Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';

@Injectable()
export class TranslatedTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly translocoService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private titleKey: string | null = null;

  constructor() {
    super();

    this.translocoService.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateBrowserTitle());
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.titleKey = findDeepestTitleKey(snapshot.root);
    this.updateBrowserTitle();
  }

  private updateBrowserTitle(): void {
    const appName =
      this.translocoService.getTranslation(this.translocoService.getActiveLang())['app.name'] ??
      this.translocoService.getTranslation('en')['app.name'] ??
      'New Nexus App';
    const pageTitle = this.titleKey ? this.translocoService.translate(this.titleKey) : null;

    this.title.setTitle(formatBrowserTitle(pageTitle, appName));
  }
}

export function findDeepestTitleKey(route: ActivatedRouteSnapshot): string | null {
  let titleKey: string | null = null;
  let currentRoute: ActivatedRouteSnapshot | null = route;

  while (currentRoute) {
    const candidate = currentRoute.data?.['titleKey'];

    if (typeof candidate === 'string') {
      titleKey = candidate;
    }

    currentRoute = currentRoute.firstChild;
  }

  return titleKey;
}

export function formatBrowserTitle(pageTitle: string | null, appName: string): string {
  return pageTitle && pageTitle !== appName ? `${pageTitle} | ${appName}` : appName;
}
